// patient-measure-evidence-dialog.component.ts
import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { DashboardService } from '@services/dashboard.service';

// Interface for measure evidence data
export interface MeasureEvidence {
  patientMRN: string;
  age: number;
  gender: string;
  emrEncounterID: string;
  encounterDate: Date;
  componentCode: string;
  evidenceGroupCode: string;
  evidenceType: string;
  codeSystemName: string;
  code: string;
  codeName: string;
  modifier: string;
  resultValue: string;
  referenceEncounterDate: Date;
  referenceEvidenceType: string;
  referenceCodeSystemName: string;
  referenceCode: string;
  referenceCodeName: string;
  referenceModifier: string;
  referenceResultValue: string;
  providerFirstName: string;
  providerLastName: string;
  serviceLocationName: string;
}

// Dialog data interface
export interface MeasureEvidenceDialogData {
  orgID: string;
  createDate: string;
  measureCode: string;
  patientMRN: string;
  patientID: number;
  measureYear: number;
}

@Component({
  selector: 'app-patient-measure-evidence-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './patient-measure-evidence-dialog.component.html',
  styleUrls: ['./patient-measure-evidence-dialog.component.css']
})
export class PatientMeasureEvidenceDialogComponent implements OnInit {
  evidence: MeasureEvidence | null = null;
  allEvidence: MeasureEvidence[] = [];
  currentIndex: number = 0;
  isLoading = false;
  hasError = false;
  errorMessage = '';

  constructor(
    private dashboardService: DashboardService,
    public dialogRef: MatDialogRef<PatientMeasureEvidenceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MeasureEvidenceDialogData
  ) {}

  ngOnInit(): void {
    this.loadMeasureEvidence();
  }

  loadMeasureEvidence(): void {
    this.isLoading = true;
    this.hasError = false;

    this.dashboardService.getMeasureEvidence(this.data.orgID, this.data.patientID, this.data.createDate, this.data.measureCode, this.data.measureYear)
      .pipe(
        catchError(error => {
          this.hasError = true;
          this.isLoading = false;
          this.errorMessage = `Failed to load measure evidence: ${error.message || 'Unknown error'}`;
          return throwError(() => error);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (data) => {
          this.allEvidence = data;
          this.currentIndex = 0;
          this.isLoading = false;
        }
      });
  }

  // Get current evidence record
  getEvidence(): MeasureEvidence | null {
    if (this.allEvidence.length > 0) {
      this.evidence = this.allEvidence[this.currentIndex];
      return this.evidence;
    }
    return null;
  }

  // Keep the misspelled method for backward compatibility
  getEvicence(): MeasureEvidence | null {
    return this.getEvidence();
  }

  // Navigation methods
  nextEvidence(): void {
    if (this.currentIndex < this.allEvidence.length - 1) {
      this.currentIndex++;
      this.evidence = this.allEvidence[this.currentIndex];
    }
  }

  previousEvidence(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.evidence = this.allEvidence[this.currentIndex];
    }
  }

  // Helper method to check if navigation is available
  hasMultipleEvidence(): boolean {
    return this.allEvidence.length > 1;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  getRefTypeLabel(evidenceType: string | undefined): string {
    if (!evidenceType) {
      return '';
    }
    
    switch (evidenceType) {
     
      
      case 'A':
        return 'Allergy/Intolerance';
      case 'C':
        return 'Encounter Diagnosis';
      case 'D':
        return 'Patient History - Diagnosis';
      case 'F':
        return 'Family History';
      case 'I':
        return 'Immunization';
      case 'L':
        return 'Data Enrichment';
      case 'M':
        return 'Medication';
      case 'N':
        return 'Nursing/Medical Order';
      case 'O':
        return 'Observation';
      case 'P':
        return 'Encounter Procedure';
      case 'R':
        return 'Allergy/Intolerance Reaction';
      case 'S':
        return 'Social History';
      case 'T':
        return 'Patient History - Procedure';
      default:
        return evidenceType;
    }
  }
}

